import {Button, Form, FormInstance, Input, Modal, Spin} from 'antd';
import {useEffect, useMemo} from 'react';

import {Locale} from '../../../../provider/locale/locale';
import {useLocale} from '../../../../provider/locale/locale-hook';
import {useSnackbar} from '../../../../provider/snackbar/snackbar-hook';
import {
    defaultExportConstructorLimits,
    useCreateExportTemplateMutation,
    useExportColumns,
    useExportLimits,
    useExportTemplate,
    useExportTemplates,
    useUpdateExportTemplateMutation,
} from '../../../../service/reviews/export-constructor/export-constructor-hook';

import {ExportTemplateAdditionalDataFields} from './components/export-template-additional-data-fields/export-template-additional-data-fields';
import {
    ExportTemplateColumnOptions,
    ExportTemplateColumnPicker,
} from './components/export-template-column-picker/export-template-column-picker';
import {ExportTemplatePreviewTable} from './components/export-template-preview-table/export-template-preview-table';
import {
    buildEditorTemplateColumns,
    exportConstructorEditorFormId,
    findParentTemplate,
    getEditorFormInitialValues,
    getEditorPickerColumns,
    isExportConstructorPivotColumnId,
    resolveParentTemplateId,
} from './reviews-export-constructor-helper';
import {
    ExportConstructorEditorFormEnum,
    ExportConstructorEditorFormType,
    ExportConstructorEditorStateType,
} from './reviews-export-constructor-types';
import * as styles from './reviews-export-template-editor-modal.module.scss';

type PropsType = {
    editorState: ExportConstructorEditorStateType | null;
    isOpen: boolean;
    onClose: () => void;
    onSaved: () => void;
};

// eslint-disable-next-line max-statements, complexity, sonarjs/cognitive-complexity
export function ReviewsExportTemplateEditorModal(props: PropsType): JSX.Element {
    const {editorState, isOpen, onClose, onSaved} = props;

    const {getLocalizedString} = useLocale();
    const {snackbar} = useSnackbar();
    const [form]: [FormInstance<ExportConstructorEditorFormType>] = Form.useForm();

    const selectedColumnIds = Form.useWatch(ExportConstructorEditorFormEnum.SelectedColumnIds, form) ?? [];

    const {data: apiColumns, isLoading: isCatalogLoading, isFetched: isColumnsFetched} = useExportColumns(isOpen);
    const {data: limits} = useExportLimits(isOpen);
    const {data: apiTemplates} = useExportTemplates(isOpen);

    const catalog = useMemo(() => apiColumns ?? [], [apiColumns]);

    const sourceTemplateId =
        editorState?.templateId ??
        (editorState?.sourceTemplateId && editorState.sourceTemplateId > 0 ? editorState.sourceTemplateId : null);
    const shouldFetchSourceTemplate = Boolean(sourceTemplateId) && !editorState?.sourceTemplate;

    const {data: fetchedSourceTemplate, isFetching: isTemplateFetching} = useExportTemplate(
        sourceTemplateId,
        isOpen && shouldFetchSourceTemplate
    );
    const sourceTemplate = editorState?.sourceTemplate ?? fetchedSourceTemplate;

    const {mutate: createTemplate, isLoading: isCreating} = useCreateExportTemplateMutation();
    const {mutate: updateTemplate, isLoading: isUpdating} = useUpdateExportTemplateMutation();

    const userTemplatesCount = (apiTemplates ?? []).filter((template) => !template.isSystem).length;

    const columnSourceTemplate = sourceTemplate;

    const parentTemplate = useMemo(() => {
        if (!columnSourceTemplate) {
            return null;
        }

        if (columnSourceTemplate.isSystem) {
            return columnSourceTemplate;
        }

        return findParentTemplate(columnSourceTemplate.parent, apiTemplates ?? []);
    }, [apiTemplates, columnSourceTemplate]);

    const columnsTemplate = parentTemplate ?? columnSourceTemplate;

    const pickerColumns = useMemo(() => {
        if (!columnsTemplate) {
            return [];
        }

        return getEditorPickerColumns(catalog, columnsTemplate);
    }, [catalog, columnsTemplate]);

    const pickerColumnIds = useMemo(() => pickerColumns.map((column) => column.id), [pickerColumns]);

    const parentColumnIds = useMemo(
        () => new Set(columnsTemplate?.columns.map((column) => column.id) ?? []),
        [columnsTemplate]
    );

    const hasTagsColumn = parentColumnIds.has('tags');
    const hasAutotagsColumn = parentColumnIds.has('autotags');
    const hasReviewsSummaryPivot = parentColumnIds.has('reviews_summary_pivot');
    const hasTagsStatPivot = parentColumnIds.has('tags_pivot');

    const isEditMode = editorState?.mode === 'edit';
    const isLimitReached =
        !isEditMode && userTemplatesCount >= (limits ?? defaultExportConstructorLimits).maxUserTemplates;

    const isEditorLoading =
        (isCatalogLoading && !isColumnsFetched) || (shouldFetchSourceTemplate && isTemplateFetching);

    useEffect(() => {
        if (!isOpen || !editorState) {
            return;
        }

        if (pickerColumnIds.length === 0) {
            return;
        }

        if (shouldFetchSourceTemplate && isTemplateFetching) {
            return;
        }

        form.setFieldsValue(
            getEditorFormInitialValues({
                isEditMode,
                availableColumnIds: pickerColumnIds,
                columnSourceTemplate,
            })
        );
    }, [
        columnSourceTemplate,
        editorState,
        form,
        isEditMode,
        isOpen,
        isTemplateFetching,
        pickerColumnIds,
        shouldFetchSourceTemplate,
    ]);

    function handleSaveSuccess() {
        onSaved();
        onClose();
    }

    function handleSubmit(formValues: ExportConstructorEditorFormType) {
        const {
            name,
            selectedColumnIds: columnIds,
            tagsFormat,
            autotagsFormat,
            withReviewsSummary,
            withTagsStat,
        } = formValues;

        if (columnIds.length === 0) {
            snackbar.error(<Locale stringKey="REVIEWS__EXPORT_CONSTRUCTOR__COLUMNS_REQUIRED" />);

            return;
        }

        const finalColumns = buildEditorTemplateColumns({
            selectedColumnIds: columnIds.filter((columnId) => !isExportConstructorPivotColumnId(columnId)),
            tagsFormat,
            autotagsFormat,
            withReviewsSummary: hasReviewsSummaryPivot ? withReviewsSummary : false,
            withTagsStat: hasTagsStatPivot ? withTagsStat : false,
        });

        if (isEditMode && editorState?.templateId) {
            updateTemplate(
                {
                    templateId: editorState.templateId,
                    payload: {name, columns: finalColumns},
                },
                {onSuccess: handleSaveSuccess}
            );

            return;
        }

        if (isLimitReached) {
            snackbar.error(<Locale stringKey="REVIEWS__EXPORT_CONSTRUCTOR__LIMIT_REACHED" />);

            return;
        }

        const parentId = columnSourceTemplate ? resolveParentTemplateId(columnSourceTemplate) : null;

        if (!parentId) {
            snackbar.error(getLocalizedString('REVIEWS__DOWNLOAD_REPORT_POPUP__THE_APPLICATION_ERROR_SENT'));

            return;
        }

        createTemplate({name: name.trim(), columns: finalColumns, parent: parentId}, {onSuccess: handleSaveSuccess});
    }

    const titleStringKey = isEditMode
        ? 'REVIEWS__EXPORT_CONSTRUCTOR__EDIT_TITLE'
        : 'REVIEWS__EXPORT_CONSTRUCTOR__CREATE_TITLE';

    return (
        <Modal
            className={styles.ReviewsExportTemplateEditorModal}
            destroyOnClose
            footer={[
                <Button key="cancel" onClick={onClose}>
                    <Locale stringKey="POPUP__BUTTON__CANCEL" />
                </Button>,
                <Button
                    disabled={isLimitReached}
                    form={exportConstructorEditorFormId}
                    htmlType="submit"
                    key="submit"
                    loading={isCreating || isUpdating}
                    type="primary"
                >
                    <Locale stringKey="REVIEWS__EXPORT_CONSTRUCTOR__BUTTON__SAVE" />
                </Button>,
            ]}
            onCancel={onClose}
            open={isOpen}
            title={<Locale stringKey={titleStringKey} />}
            width="1000px"
        >
            <Spin spinning={isEditorLoading}>
                <Form<ExportConstructorEditorFormType>
                    form={form}
                    id={exportConstructorEditorFormId}
                    layout="vertical"
                    onFinish={handleSubmit}
                >
                    <Form.Item<ExportConstructorEditorFormType>
                        label={<Locale stringKey="REVIEWS__DOWNLOAD_REPORT_POPUP__NAME__LABEL__TITLE" />}
                        name={ExportConstructorEditorFormEnum.Name}
                        rules={[
                            {
                                required: true,
                                message: getLocalizedString('REVIEWS__EXPORT_CONSTRUCTOR__NAME_REQUIRED'),
                            },
                        ]}
                    >
                        <Input placeholder={getLocalizedString('REVIEWS__EXPORT_CONSTRUCTOR__NAME_PLACEHOLDER')} />
                    </Form.Item>

                    <div className={styles.ReviewsExportTemplateEditorModal_section}>
                        <div className={styles.ReviewsExportTemplateEditorModal_sectionTitle}>
                            <Locale stringKey="REVIEWS__EXPORT_CONSTRUCTOR__COLUMNS_LABEL" />
                        </div>
                        <ExportTemplateColumnPicker catalog={pickerColumns} />
                    </div>

                    {hasTagsColumn ? (
                        <ExportTemplateColumnOptions
                            labelStringKey="REVIEWS__EXPORT_CONSTRUCTOR__TAGS_FORMAT_LABEL"
                            name={ExportConstructorEditorFormEnum.TagsFormat}
                        />
                    ) : null}

                    {hasAutotagsColumn ? (
                        <ExportTemplateColumnOptions
                            labelStringKey="REVIEWS__EXPORT_CONSTRUCTOR__AUTOTAGS_FORMAT_LABEL"
                            name={ExportConstructorEditorFormEnum.AutotagsFormat}
                        />
                    ) : null}

                    {hasReviewsSummaryPivot || hasTagsStatPivot ? (
                        <div className={styles.ReviewsExportTemplateEditorModal_section}>
                            <ExportTemplateAdditionalDataFields<ExportConstructorEditorFormType>
                                reviewsSummaryName={ExportConstructorEditorFormEnum.WithReviewsSummary}
                                showReviewsSummary={hasReviewsSummaryPivot}
                                showTagsStat={hasTagsStatPivot}
                                tagsStatName={ExportConstructorEditorFormEnum.WithTagsStat}
                            />
                        </div>
                    ) : null}

                    <div className={styles.ReviewsExportTemplateEditorModal_section}>
                        <ExportTemplatePreviewTable
                            catalog={pickerColumns}
                            onReorder={(columnIds) => {
                                form.setFieldValue(ExportConstructorEditorFormEnum.SelectedColumnIds, columnIds);
                            }}
                            selectedColumnIds={selectedColumnIds}
                        />
                    </div>
                </Form>
            </Spin>
        </Modal>
    );
}
